import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as API from '../../store/sagas/api';
import * as userActions from '../../store/actions/user';
import DatePicker from '../../components/DatePicker';
import ErrorMessage from '../../components/ErrorMessage';
import NewAddressAutocomplete from '../../components/NewAddressAutocomplete';
import PhoneNumberInput, { isValidE164Phone } from '../../components/PhoneNumberInput';
import SearchableSelect from '../../components/SearchableSelect';
import { DocumentReviewIcon, ReviewRow, ReviewSection, ReviewStepShell } from '../../components/OnboardingReview';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';
import {
  getOnboardingStepPath,
  getPostCompanyPersonDataNextPath,
  isKycOrLaterOnboardingStep,
} from '../../utils/onboardingUtils';
import { UserType } from '../../enums';
import { TrashIcon, PlusIcon } from '../../assets/icons';
import { COMPANY_DOC_TYPE_KEY, readDocumentSubtypeFromMetadata, toPascalCaseEnumValue } from '../../utils/companyDocumentCoverage';

const inputClass =
  'h-11 w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-[3px] focus:ring-[#2E6BAD]/[.12] disabled:cursor-not-allowed disabled:opacity-50';
const inputErrorBorderClass = '!border-[#C0392B] border-2';
const searchableSelectTriggerClass = (hasError = false) =>
  `${inputClass} flex items-center gap-2 text-left ${hasError ? inputErrorBorderClass : ''}`;
const fieldLabelClass = 'mb-1.5 block text-[11.5px] font-semibold normal-case tracking-normal text-[#7A9AB0]';
const fieldGroupClass = 'mb-4';
const fieldErrorClass = 'mt-1.5 text-xs text-[#C0392B]';
const addPersonButtonClass =
  'mt-3.5 flex w-full items-center justify-center gap-2 rounded-[16px] border-[1.5px] border-dashed border-[#C8D9E8] bg-white px-4 py-3.5 text-sm font-medium text-[#2E6BAD] transition hover:border-[#7A9AB0] hover:bg-[#EBF4FB]';
const PROOF_ADDRESS_METADATA_TYPE = 'proofAddress';
const PROOF_ADDRESS_TYPES = [
  { value: 'BankStatement', label: 'Bank statement' },
  { value: 'UtilityBill', label: 'Utility bill' },
  { value: 'GovernmentIssuedLetter', label: 'Government-issued letter' },
  { value: 'ResidentialLeaseAgreement', label: 'Residential lease agreement' },
  { value: 'TaxDocument', label: 'Tax document' },
];
const CONTROLLING_PERSON_ROLES = [
  'CEO',
  'CFO',
  'COO',
  'President',
  'Director',
  'Chairman of the Board',
];
const CONTROLLING_PERSON_ROLE_OPTIONS = CONTROLLING_PERSON_ROLES.map((role) => ({ value: role, label: role }));

const COMPANY_PERSON_FIELD_ERROR_MESSAGES = {
  firstName: 'First name is required',
  lastName: 'Last name is required',
  email: 'Enter a valid email',
  phoneNumber: 'Enter a valid phone number (E.164 format, e.g. +491701234567)',
  birthDate: 'Birth date is required',
  'birthDate.underage': 'You must be at least 18 years old',
  companyRole: 'Role is required',
  companyStartDate: 'Relationship start day with the company is required',
  companyOwnershipPercentage: 'Ownership % is required and must be a whole number from 0 to 100',
  'address.line1': 'Address line 1 is required',
  'address.city': 'City is required',
  'address.stateRegionProvince': 'State / Region / Province is required',
  'address.postCode': 'Post code is required',
  'address.country': 'Country is required',
};
let personLocalIdCounter = 0;

function nextPersonLocalId() {
  personLocalIdCounter += 1;
  return `person-${personLocalIdCounter}`;
}

function isValidEmail(s) {
  if (!s || typeof s !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function isValidPhone(s) {
  return isValidE164Phone(s);
}

function getAdultBirthDateLimit() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 18);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function makeEmptyPerson(type) {
  const isControllingType = type === 'ControllingPerson' || type === 'BeneficialControlling';
  return {
    localId: nextPersonLocalId(),
    id: '',
    companyPersonType: type,
    addressSearch: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthDate: '',
    companyRole: '',
    isDirector: isControllingType,
    companyOwnershipPercentage: '',
    companyStartDate: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      stateRegionProvince: '',
      postCode: '',
      country: '',
    },
  };
}

function normalizeAddress(a) {
  if (!a) return null;
  return {
    line1: a.line1?.trim() ?? '',
    line2: a.line2?.trim() || null,
    city: (a.city ?? '').trim(),
    stateRegionProvince: (a.stateRegionProvince ?? '').trim(),
    postCode: a.postCode?.trim() ?? '',
    country: (a.country ?? '').trim().toUpperCase(),
  };
}

function normalizeMiddleName(s) {
  const t = (s ?? '').trim();
  return t || null;
}

function mapApiPersonToForm(p) {
  const isControllingType = p.companyPersonType === 'ControllingPerson' || p.companyPersonType === 'BeneficialControlling';
  const rawRole = (p.companyRole ?? '').trim();
  let roleForControlling = '';
  if (isControllingType) {
    roleForControlling = CONTROLLING_PERSON_ROLES.includes(rawRole) ? rawRole : '';
  }
  return {
    localId: nextPersonLocalId(),
    id: p.id ?? '',
    companyPersonType: p.companyPersonType,
    addressSearch: '',
    firstName: p.firstName ?? '',
    middleName: p.middleName ?? '',
    lastName: p.lastName ?? '',
    email: p.email ?? '',
    phoneNumber: p.phoneNumber ?? '',
    birthDate: p.birthDate ?? '',
    companyRole: isControllingType ? roleForControlling : '',
    companyOwnershipPercentage: p.companyOwnershipPercentage ?? '',
    companyStartDate: p.companyStartDate ?? '',
    isDirector: p.isDirector ?? false,
    address: {
      line1: p.address?.line1 ?? '',
      line2: p.address?.line2 ?? '',
      city: p.address?.city ?? '',
      stateRegionProvince: p.address?.stateRegionProvince ?? '',
      postCode: p.address?.postCode ?? '',
      country: p.address?.country ?? '',
    },
  };
}

function normalizeEmailKey(email) {
  return String(email ?? '')
    .trim()
    .toLowerCase();
}

function splitApiPersonsToForm(persons) {
  const list = Array.isArray(persons) ? persons : [];
  const reps = list
    .filter((p) => p.companyPersonType === 'ControllingPerson' || p.companyPersonType === 'BeneficialControlling')
    .map(mapApiPersonToForm);
  const owns = list.filter((p) => p.companyPersonType === 'BeneficialOwner').map(mapApiPersonToForm);
  const nextReps = reps.length > 0 ? reps : [makeEmptyPerson('ControllingPerson')];
  const selectedIds = new Set(nextReps.filter((p) => p.companyPersonType === 'BeneficialControlling').map((p) => p.localId));
  const selectedEmails = new Set(
    nextReps
      .filter((p) => p.companyPersonType === 'BeneficialControlling')
      .map((p) => normalizeEmailKey(p.email))
      .filter(Boolean),
  );
  const nextOwners = owns.filter((o) => !selectedEmails.has(normalizeEmailKey(o.email)));
  const requiredUboPersons = list.filter(
    (p) => p?.companyPersonType === 'BeneficialOwner' || p?.companyPersonType === 'BeneficialControlling',
  );
  return { nextReps, nextOwners, selectedIds, requiredUboPersons };
}

function PersonFields({
  title,
  person,
  onChange,
  fieldPrefix,
  showValidation,
  validationErrors,
  disableIdentityFields = false,
  disabled = false,
  showCompanyRoleField = false,
  showOwnershipField = false,
  onRemove = undefined,
  collapsed = false,
  addressAllowedCountries = [],
}) {
  const personAddressAutocompletePrimaryTypes = useMemo(() => ['street_address', 'route', 'premise', 'subpremise'], []);
  const fieldsDisabled = disableIdentityFields || disabled;
  const maxBirthDate = useMemo(() => getAdultBirthDateLimit(), []);
  const isInvalid = (key) => {
    if (key === 'birthDate') {
      return (
        showValidation &&
        (validationErrors.has(`${fieldPrefix}.birthDate`) || validationErrors.has(`${fieldPrefix}.birthDate.underage`))
      );
    }
    return showValidation && validationErrors.has(`${fieldPrefix}.${key}`);
  };
  const fieldId = (name) => `${fieldPrefix}-${name}`.replace(/\./g, '-');
  const getErrorMessage = (key) => {
    if (key === 'birthDate' && validationErrors.has(`${fieldPrefix}.birthDate.underage`)) {
      return COMPANY_PERSON_FIELD_ERROR_MESSAGES['birthDate.underage'];
    }
    return COMPANY_PERSON_FIELD_ERROR_MESSAGES[key] || 'Required';
  };
  const handleAddressSearchSelect = (selected) => {
    const components = selected?.components || {};
    const streetLine = [components.route, components.streetNumber].filter(Boolean).join(' ').trim();
    const nextCity = (components.city || '').trim();
    const nextState = (components.state || '').trim();
    const nextPost = (components.postalCode || '').trim();
    const nextCountry = (components.country || '').trim().toUpperCase();
    const isBackfill = selected?.source === 'backfill';
    onChange({
      ...person,
      addressSearch: isBackfill ? person.addressSearch : (selected?.fullAddress || '').trim(),
      address: {
        ...person.address,
        line1: isBackfill ? person.address.line1 : streetLine || (selected?.fullAddress || '').trim() || person.address.line1,
        city: nextCity,
        stateRegionProvince: nextState,
        postCode: nextPost,
        country: nextCountry,
      },
    });
  };

  const fullName = [person.firstName, person.middleName, person.lastName].filter((v) => String(v ?? '').trim().length > 0).join(' ');
  return (
    <div className='mb-3.5 rounded-[16px] border-[1.5px] border-[#E2EBF3] bg-white px-6 py-[22px]'>
      <div className='mb-5 flex items-center gap-3'>
        <div className='flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border-2 border-[#DDE6EF] bg-[#EBF4FB] text-[#7A9AB0]'>
          <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <circle cx='12' cy='8' r='4' />
            <path d='M4 20c0-4 3.6-7 8-7s8 3 8 7' />
          </svg>
        </div>
        <div className='min-w-0 flex-1'>
          <div className='text-sm font-semibold text-[#0B1929]'>{title}</div>
          <div className='truncate text-[12.5px] text-[#4E6F8A]'>{fullName || '—'}</div>
        </div>
        {onRemove && !disabled ? (
          <button
            type='button'
            onClick={onRemove}
            className='inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#C0392B] transition hover:bg-[#C0392B]/10'
            aria-label={`Delete ${title}`}
          >
            <TrashIcon className='h-4 w-4' />
          </button>
        ) : null}
      </div>
      {!collapsed ? (
        <div>
          <div className='grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-3'>
            <div className={fieldGroupClass}>
              <label htmlFor={fieldId('firstName')} className={fieldLabelClass}>
                First name *
              </label>
              <input
                id={fieldId('firstName')}
                type='text'
                className={`${inputClass} ${isInvalid('firstName') ? inputErrorBorderClass : ''}`}
                value={person.firstName}
                onChange={(e) => onChange({ ...person, firstName: e.target.value })}
                disabled={fieldsDisabled}
                placeholder='First name'
              />
              {isInvalid('firstName') && <p className={fieldErrorClass}>{getErrorMessage('firstName')}</p>}
            </div>
            <div className={fieldGroupClass}>
              <label htmlFor={fieldId('middleName')} className={fieldLabelClass}>
                Middle name
              </label>
              <input
                id={fieldId('middleName')}
                type='text'
                className={inputClass}
                value={person.middleName}
                onChange={(e) => onChange({ ...person, middleName: e.target.value })}
                disabled={fieldsDisabled}
                placeholder='Middle name'
              />
            </div>
          </div>
          <div className={fieldGroupClass}>
            <label htmlFor={fieldId('lastName')} className={fieldLabelClass}>
              Last name *
            </label>
            <input
              id={fieldId('lastName')}
              type='text'
              className={`${inputClass} ${isInvalid('lastName') ? inputErrorBorderClass : ''}`}
              value={person.lastName}
              onChange={(e) => onChange({ ...person, lastName: e.target.value })}
              disabled={fieldsDisabled}
              placeholder='Last name'
            />
            {isInvalid('lastName') && <p className={fieldErrorClass}>{getErrorMessage('lastName')}</p>}
          </div>
          <div className={fieldGroupClass}>
            <label htmlFor={fieldId('email')} className={fieldLabelClass}>
              Email *
            </label>
            <input
              id={fieldId('email')}
              type='email'
              className={`${inputClass} ${isInvalid('email') ? inputErrorBorderClass : ''}`}
              value={person.email}
              onChange={(e) => onChange({ ...person, email: e.target.value })}
              disabled={fieldsDisabled}
              placeholder='person@company.com'
            />
            {isInvalid('email') && <p className={fieldErrorClass}>{getErrorMessage('email')}</p>}
          </div>
          <div className={fieldGroupClass}>
            <label htmlFor={fieldId('birthDate')} className={fieldLabelClass}>
              Birth date *
            </label>
            <DatePicker
              id={fieldId('birthDate')}
              name='birthDate'
              className={`${inputClass} ${isInvalid('birthDate') ? inputErrorBorderClass : ''}`}
              value={person.birthDate}
              max={maxBirthDate}
              onChange={(e) => onChange({ ...person, birthDate: e.target.value })}
              disabled={fieldsDisabled}
            />
            {isInvalid('birthDate') && <p className={fieldErrorClass}>{getErrorMessage('birthDate')}</p>}
          </div>
          <div className={fieldGroupClass}>
            <label htmlFor={fieldId('phoneNumber')} className={fieldLabelClass}>
              Phone number *
            </label>
            <PhoneNumberInput
              id={fieldId('phoneNumber')}
              value={person.phoneNumber}
              onChange={(nextValue) => onChange({ ...person, phoneNumber: nextValue })}
              disabled={fieldsDisabled}
              inputClassName={`${inputClass} ${isInvalid('phoneNumber') ? inputErrorBorderClass : ''}`}
              selectClassName={`${inputClass} pr-8 ${isInvalid('phoneNumber') ? inputErrorBorderClass : ''}`}
              placeholder='e.g. 1701234567'
            />
            {isInvalid('phoneNumber') && <p className={fieldErrorClass}>{getErrorMessage('phoneNumber')}</p>}
          </div>
          <div className={`${fieldGroupClass} relative ${fieldsDisabled ? 'pointer-events-none opacity-50' : ''}`}>
            <label htmlFor={fieldId('address-search')} className={fieldLabelClass}>
              Address search
            </label>
            <NewAddressAutocomplete
              id={fieldId('address-search')}
              value={person.addressSearch}
              onChange={(v) => onChange({ ...person, addressSearch: v })}
              onAddressSelect={handleAddressSearchSelect}
              placeholder='Search for an address'
              className='w-full'
              includedPrimaryTypes={personAddressAutocompletePrimaryTypes}
              allowedCountries={addressAllowedCountries}
            />
            <p className='mt-1 text-[11.5px] text-[#7A9AB0]'>Or fill in manually below.</p>
          </div>
          <div className={fieldGroupClass}>
            <label htmlFor={fieldId('address-line1')} className={fieldLabelClass}>
              Address line 1 *
            </label>
            <input
              id={fieldId('address-line1')}
              type='text'
              className={`${inputClass} ${isInvalid('address.line1') ? inputErrorBorderClass : ''}`}
              value={person.address.line1}
              onChange={(e) => onChange({ ...person, address: { ...person.address, line1: e.target.value } })}
              disabled={fieldsDisabled}
              placeholder='Street and number'
            />
            {isInvalid('address.line1') && <p className={fieldErrorClass}>{getErrorMessage('address.line1')}</p>}
          </div>
          <div className={fieldGroupClass}>
            <label htmlFor={fieldId('address-line2')} className={fieldLabelClass}>
              Address line 2
            </label>
            <input
              id={fieldId('address-line2')}
              type='text'
              className={inputClass}
              value={person.address.line2}
              onChange={(e) => onChange({ ...person, address: { ...person.address, line2: e.target.value } })}
              disabled={fieldsDisabled}
            />
          </div>
          <div className='grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-3'>
            <div className={fieldGroupClass}>
              <label htmlFor={fieldId('address-city')} className={fieldLabelClass}>
                City *
              </label>
              <input
                id={fieldId('address-city')}
                type='text'
                className={`${inputClass} ${isInvalid('address.city') ? inputErrorBorderClass : ''}`}
                value={person.address.city}
                onChange={(e) => onChange({ ...person, address: { ...person.address, city: e.target.value } })}
                disabled={fieldsDisabled}
                placeholder='City'
              />
              {isInvalid('address.city') && <p className={fieldErrorClass}>{getErrorMessage('address.city')}</p>}
            </div>
            <div className={fieldGroupClass}>
              <label htmlFor={fieldId('address-postCode')} className={fieldLabelClass}>
                Post code *
              </label>
              <input
                id={fieldId('address-postCode')}
                type='text'
                className={`${inputClass} ${isInvalid('address.postCode') ? inputErrorBorderClass : ''}`}
                value={person.address.postCode}
                onChange={(e) => onChange({ ...person, address: { ...person.address, postCode: e.target.value } })}
                disabled={fieldsDisabled}
                placeholder='Post code'
              />
              {isInvalid('address.postCode') && <p className={fieldErrorClass}>{getErrorMessage('address.postCode')}</p>}
            </div>
          </div>
          <div className='grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-3'>
            <div className={fieldGroupClass}>
              <label htmlFor={fieldId('address-stateRegionProvince')} className={fieldLabelClass}>
                State / Region / Province *
              </label>
              <input
                id={fieldId('address-stateRegionProvince')}
                type='text'
                className={`${inputClass} ${isInvalid('address.stateRegionProvince') ? inputErrorBorderClass : ''}`}
                value={person.address.stateRegionProvince}
                onChange={(e) => onChange({ ...person, address: { ...person.address, stateRegionProvince: e.target.value } })}
                disabled={fieldsDisabled}
              />
              {isInvalid('address.stateRegionProvince') && <p className={fieldErrorClass}>{getErrorMessage('address.stateRegionProvince')}</p>}
            </div>
            <div className={fieldGroupClass}>
              <label htmlFor={fieldId('country')} className={fieldLabelClass}>
                Country *
              </label>
              <input
                id={fieldId('country')}
                value={person.address.country}
                onChange={(e) => onChange({ ...person, address: { ...person.address, country: String(e.target.value || '').toUpperCase() } })}
                className={`${inputClass} ${isInvalid('address.country') ? inputErrorBorderClass : ''}`}
                disabled={fieldsDisabled}
                placeholder='e.g. DE'
              />
              {isInvalid('address.country') && <p className={fieldErrorClass}>{getErrorMessage('address.country')}</p>}
            </div>
          </div>

          <div className={fieldGroupClass}>
            <label htmlFor={fieldId('companyStartDate')} className={fieldLabelClass}>
              Relationship start day with the company *
            </label>
            <DatePicker
              id={fieldId('companyStartDate')}
              name='companyStartDate'
              className={`${inputClass} ${isInvalid('companyStartDate') ? inputErrorBorderClass : ''}`}
              value={person.companyStartDate}
              onChange={(e) => onChange({ ...person, companyStartDate: e.target.value })}
              disabled={fieldsDisabled}
            />
            {isInvalid('companyStartDate') && <p className={fieldErrorClass}>{getErrorMessage('companyStartDate')}</p>}
          </div>

          {showCompanyRoleField || showOwnershipField ? (
            <div className='mt-1 border-t border-[#EEF3F8] pt-4'>
              {showCompanyRoleField ? (
                <div className='mb-1'>
                  <div className='mb-3 text-[10px] font-bold uppercase tracking-[0.1em] text-[#7A9AB0]/70'>Controlling</div>
                  <div className={fieldGroupClass}>
                    <label htmlFor={fieldId('companyRole')} className={fieldLabelClass}>
                      Role *
                    </label>
                    <SearchableSelect
                      id={fieldId('companyRole')}
                      name='companyRole'
                      value={person.companyRole}
                      onChange={(e) => onChange({ ...person, companyRole: e.target.value })}
                      placeholder='Select role…'
                      options={CONTROLLING_PERSON_ROLE_OPTIONS}
                      searchPlaceholder='Search role...'
                      menuPlacement='auto'
                      menuMinWidth='w-full'
                      clearable
                      hideValue
                      disabled={fieldsDisabled}
                      hasError={isInvalid('companyRole')}
                      triggerClassName={searchableSelectTriggerClass(isInvalid('companyRole'))}
                    />
                    {isInvalid('companyRole') && <p className={fieldErrorClass}>{getErrorMessage('companyRole')}</p>}
                  </div>
                  <div className='mt-3 flex items-center gap-2.5'>
                    <input
                      id={fieldId('isDirector')}
                      type='checkbox'
                      checked={!!person.isDirector}
                      onChange={(e) => onChange({ ...person, isDirector: e.target.checked })}
                      disabled={fieldsDisabled}
                      className='h-4 w-4 shrink-0 cursor-pointer accent-[#2E6BAD] disabled:cursor-not-allowed'
                    />
                    <label htmlFor={fieldId('isDirector')} className='cursor-pointer text-[13.5px] font-normal text-[#3D5A73]'>
                      Is a company director?
                    </label>
                  </div>
                  {showValidation && validationErrors.has('directors.min') && !person.isDirector ? (
                    <p className={fieldErrorClass}>At least one company management person must be a director.</p>
                  ) : null}
                </div>
              ) : null}
              {showOwnershipField ? (
                <div className={showCompanyRoleField ? 'mt-4' : ''}>
                  <div className='mb-3 text-[10px] font-bold uppercase tracking-[0.1em] text-[#7A9AB0]/70'>Ownership</div>
                  <div className={fieldGroupClass}>
                    <label htmlFor={fieldId('companyOwnershipPercentage')} className={fieldLabelClass}>
                      Ownership % *
                    </label>
                    <input
                      id={fieldId('companyOwnershipPercentage')}
                      type='number'
                      min={0}
                      max={100}
                      step={1}
                      className={`${inputClass} ${isInvalid('companyOwnershipPercentage') ? inputErrorBorderClass : ''}`}
                      value={person.companyOwnershipPercentage}
                      onChange={(e) => onChange({ ...person, companyOwnershipPercentage: e.target.value })}
                      disabled={fieldsDisabled}
                    />
                    <p className='mt-1 text-xs text-[#7A9AB0]'>Required. Enter ownership as a whole number from 0–100%.</p>
                    {isInvalid('companyOwnershipPercentage') && (
                      <p className={fieldErrorClass}>{getErrorMessage('companyOwnershipPercentage')}</p>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

PersonFields.propTypes = {
  title: PropTypes.string.isRequired,
  person: PropTypes.shape({
    localId: PropTypes.string,
    addressSearch: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    middleName: PropTypes.string,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    companyRole: PropTypes.string,
    phoneNumber: PropTypes.string.isRequired,
    birthDate: PropTypes.string.isRequired,
    isDirector: PropTypes.bool,
    companyOwnershipPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyStartDate: PropTypes.string.isRequired,
    address: PropTypes.shape({
      line1: PropTypes.string.isRequired,
      line2: PropTypes.string,
      city: PropTypes.string.isRequired,
      stateRegionProvince: PropTypes.string,
      postCode: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  fieldPrefix: PropTypes.string.isRequired,
  showValidation: PropTypes.bool.isRequired,
  validationErrors: PropTypes.instanceOf(Set).isRequired,
  disableIdentityFields: PropTypes.bool,
  disabled: PropTypes.bool,
  showOwnershipField: PropTypes.bool,
  showCompanyRoleField: PropTypes.bool,
  onRemove: PropTypes.func,
  collapsed: PropTypes.bool,
  addressAllowedCountries: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ code: PropTypes.string })])),
};

function CollectCompanyPersonData() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const setFooterConfig = useOnboardingFooter();
  const hasJustConfirmedRef = useRef(false);
  const hasLoadedRef = useRef(false);

  const { user, onboardingStatus, advancingOnboardingStep, advanceOnboardingStepError } = useSelector((state) => state.user);
  // NOTE: Company Management phone numbers should allow the full set of countries.
  const isLocked = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);

  const [representatives, setRepresentatives] = useState([makeEmptyPerson('ControllingPerson')]);
  const [owners, setOwners] = useState([]);
  const [selectedManagementIds, setSelectedManagementIds] = useState(() => new Set());
  const [pendingScrollOwnerLocalId, setPendingScrollOwnerLocalId] = useState(null);
  const [showSummary, setShowSummary] = useState(() => location.state?.companyPersonsScreen === 'review');
  const [showProofOfAddress, setShowProofOfAddress] = useState(() => location.state?.companyPersonsScreen === 'proofAddress');
  const [uboPersons, setUboPersons] = useState([]);
  const [proofAddressByPersonId, setProofAddressByPersonId] = useState({});
  const lastSavedPayloadJsonRef = useRef(null);
  const lastSavedUboPersonsRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [loadingManagementFromSignicat, setLoadingManagementFromSignicat] = useState(false);
  const [, setCollapsedIds] = useState(new Set());
  const initialPersonsScreen = location.state?.companyPersonsScreen;
  const [activePersonsScreen, setActivePersonsScreen] = useState(() =>
    initialPersonsScreen === 'owners' || initialPersonsScreen === 'proofAddress' || initialPersonsScreen === 'review'
      ? 'owners'
      : 'representatives',
  ); // UI-only split

  useEffect(() => {
    let nextScreen = 'representatives';
    if (showSummary) nextScreen = 'review';
    else if (showProofOfAddress) nextScreen = 'proofAddress';
    else if (activePersonsScreen === 'owners') nextScreen = 'owners';

    if (location.state?.companyPersonsScreen === nextScreen) return;
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state ?? {}), companyPersonsScreen: nextScreen },
    });
  }, [activePersonsScreen, location.pathname, location.state, navigate, showProofOfAddress, showSummary]);

  useEffect(() => {
    if (!user || user.userType !== UserType.Company) return;
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    const load = async () => {
      try {
        setShowValidation(false);
        const persons = await API.getCompanyPersons();
        const { nextReps, nextOwners, selectedIds, requiredUboPersons } = splitApiPersonsToForm(persons);
        setRepresentatives(nextReps);
        setOwners(nextOwners);
        setCollapsedIds(new Set());
        setSelectedManagementIds(selectedIds);

        const requestedScreen = location.state?.companyPersonsScreen;
        if (requestedScreen === 'proofAddress' || requestedScreen === 'review') {
          setUboPersons(requiredUboPersons);
          lastSavedUboPersonsRef.current = requiredUboPersons;
          if (requestedScreen === 'proofAddress') {
            setShowProofOfAddress(true);
            setShowSummary(false);
          } else {
            setShowProofOfAddress(false);
            setShowSummary(true);
          }
          setActivePersonsScreen('owners');
        }

        const hasSavedRepresentatives = Array.isArray(persons)
          ? persons.some((p) => p.companyPersonType === 'ControllingPerson' || p.companyPersonType === 'BeneficialControlling')
          : false;
        const extId = user?.company?.signicatCompanyExternalId;
        const country = user?.company?.registeredAddress?.country ?? user?.company?.operatingAddress?.country ?? user?.company?.country ?? '';
        const canPullSignicatReps = Boolean(String(extId || '').trim() && String(country || '').trim());

        if (!hasSavedRepresentatives && canPullSignicatReps) {
          setLoadingManagementFromSignicat(true);
          try {
            const list = await API.companyRepresentatives({
              companyExternalId: String(extId).trim(),
              country: String(country).trim().toLowerCase(),
            });
            const raw = Array.isArray(list) ? list : [];
            if (raw.length === 0) return;

            const mapped = raw.map((r, idx) => {
              const p = makeEmptyPerson('ControllingPerson');
              const addr = r?.address || {};
              return {
                ...p,
                firstName: r?.firstName ?? r?.FirstName ?? '',
                lastName: r?.lastName ?? r?.LastName ?? '',
                email: r?.emailAddress ?? r?.EmailAddress ?? '',
                birthDate: r?.dateOfBirth ? String(r.dateOfBirth).slice(0, 10) : '',
                isDirector: idx === 0,
                address: {
                  ...p.address,
                  line1: addr?.line1 ?? addr?.Line1 ?? addr?.street ?? addr?.Street ?? '',
                  line2: addr?.line2 ?? addr?.Line2 ?? '',
                  city: addr?.city ?? addr?.City ?? '',
                  stateRegionProvince: addr?.stateRegionProvince ?? addr?.StateRegionProvince ?? addr?.state ?? addr?.State ?? '',
                  postCode: addr?.postCode ?? addr?.PostCode ?? addr?.postalCode ?? addr?.PostalCode ?? '',
                  country: addr?.country ?? addr?.Country ?? country ?? '',
                },
              };
            });
            setRepresentatives(mapped);
          } catch (e) {
            await Promise.resolve(e);
          } finally {
            setLoadingManagementFromSignicat(false);
          }
        }
      } catch (e) {
        setSubmitError(e?.message || e?.detail || 'Failed to load company persons.');
        setLoadingManagementFromSignicat(false);
      }
    };
    load();
  }, [user]);

  useEffect(() => {
    if (!showSummary) {
      setShowValidation(false);
    }
  }, [showSummary]);

  const toggleManagementSelection = (localId) => {
    if (isLocked) return;
    const rep = representatives.find((r) => r.localId === localId);
    if (!rep) return;

    setSelectedManagementIds((prev) => {
      const next = new Set(prev);
      const isSelected = next.has(localId);

      if (isSelected) {
        next.delete(localId);
        setRepresentatives((repsPrev) =>
          repsPrev.map((r) => {
            if (r.localId !== localId) return r;
            return { ...r, companyPersonType: 'ControllingPerson', companyOwnershipPercentage: '' };
          }),
        );
        return next;
      }

      next.add(localId);
      setRepresentatives((repsPrev) =>
        repsPrev.map((r) => {
          if (r.localId !== localId) return r;
          return {
            ...r,
            companyPersonType: 'BeneficialControlling',
            companyOwnershipPercentage: r.companyOwnershipPercentage ?? '',
          };
        }),
      );

      const repEmailKey = normalizeEmailKey(rep.email);
      if (repEmailKey) {
        setOwners((ownersPrev) => ownersPrev.filter((o) => normalizeEmailKey(o.email) !== repEmailKey));
      }
      return next;
    });
  };

  useEffect(() => {
    if (!pendingScrollOwnerLocalId) return;
    const el = document.querySelector(`[data-owner-localid="${pendingScrollOwnerLocalId}"]`);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!inView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setPendingScrollOwnerLocalId(null);
  }, [pendingScrollOwnerLocalId]);

  useEffect(() => {
    if (
      hasJustConfirmedRef.current &&
      !advancingOnboardingStep &&
      onboardingStatus?.currentStep &&
      onboardingStatus.currentStep !== 'CollectCompanyPersonData'
    ) {
      const nextPath =
        getPostCompanyPersonDataNextPath(onboardingStatus?.steps, { userType: user?.userType }) ??
        getOnboardingStepPath(onboardingStatus.currentStep);
      if (nextPath) {
        hasJustConfirmedRef.current = false;
        navigate(nextPath);
      }
    }
  }, [advancingOnboardingStep, navigate, onboardingStatus?.currentStep, onboardingStatus?.steps, user?.userType]);

  const displayError = submitError ?? advanceOnboardingStepError?.message ?? advanceOnboardingStepError?.detail ?? null;

  const validationErrors = useMemo(() => {
    const errors = new Set();
    if (representatives.length < 1) errors.add('representatives.min');
    if (representatives.length > 0 && !representatives.some((p) => p.isDirector === true)) errors.add('directors.min');
    const hasAnyOwner = owners.length > 0 || representatives.some((p) => p.companyPersonType === 'BeneficialControlling');
    if (!hasAnyOwner) errors.add('owners.min');

    const maxBirthDate = getAdultBirthDateLimit();

    const validatePerson = (p, prefix, isOwner) => {
      if (!p.firstName.trim()) errors.add(`${prefix}.firstName`);
      if (!p.lastName.trim()) errors.add(`${prefix}.lastName`);
      if (!p.email.trim() || !isValidEmail(p.email)) errors.add(`${prefix}.email`);
      if (!p.phoneNumber.trim() || !isValidPhone(p.phoneNumber)) errors.add(`${prefix}.phoneNumber`);
      if (!p.birthDate.trim()) {
        errors.add(`${prefix}.birthDate`);
      } else if (p.birthDate > maxBirthDate) {
        errors.add(`${prefix}.birthDate.underage`);
      }
      if (!isOwner) {
        const role = (p.companyRole ?? '').trim();
        if (!role || !CONTROLLING_PERSON_ROLES.includes(role)) {
          errors.add(`${prefix}.companyRole`);
        }
      }
      if (!p.companyStartDate.trim()) errors.add(`${prefix}.companyStartDate`);
      if (!p.address?.line1?.trim()) errors.add(`${prefix}.address.line1`);
      if (!p.address?.city?.trim()) errors.add(`${prefix}.address.city`);
      if (!p.address?.stateRegionProvince?.trim()) errors.add(`${prefix}.address.stateRegionProvince`);
      if (!p.address?.postCode?.trim()) errors.add(`${prefix}.address.postCode`);
      if (!p.address?.country?.trim()) errors.add(`${prefix}.address.country`);
      if (isOwner) {
        const ownership = p.companyOwnershipPercentage;
        if (ownership === null || ownership === undefined || String(ownership).trim() === '') {
          errors.add(`${prefix}.companyOwnershipPercentage`);
        } else {
          const n = Number(String(ownership).trim());
          if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > 100) {
            errors.add(`${prefix}.companyOwnershipPercentage`);
          }
        }
      }
    };

    representatives.forEach((p, idx) => validatePerson(p, `representatives.${idx}`, p.companyPersonType === 'BeneficialControlling'));
    owners.forEach((p, idx) => validatePerson(p, `owners.${idx}`, true));
    return errors;
  }, [representatives, owners]);

  const canSubmit = validationErrors.size === 0 && !saving;

  const buildPayload = useCallback(() => {
    const repPayload = representatives.map((p) => ({
      id: typeof p.id === 'string' && p.id.trim() ? p.id.trim() : '',
      companyPersonType: p.companyPersonType,
      firstName: p.firstName.trim(),
      middleName: normalizeMiddleName(p.middleName),
      lastName: p.lastName.trim(),
      email: p.email.trim(),
      phoneNumber: String(p.phoneNumber || '').replace(/\s+/g, ''),
      birthDate: p.birthDate,
      companyRole: p.companyRole.trim(),
      isDirector: p.isDirector === true,
      companyOwnershipPercentage: (() => {
        if (p.companyPersonType !== 'BeneficialControlling') return null;
        const raw = p.companyOwnershipPercentage;
        if (raw === null || raw === undefined || String(raw).trim() === '') return null;
        return Number(String(raw).trim());
      })(),
      companyStartDate: p.companyStartDate,
      address: normalizeAddress(p.address),
    }));
    const ownerPayload = owners.map((p) => ({
      id: typeof p.id === 'string' && p.id.trim() ? p.id.trim() : '',
      companyPersonType: 'BeneficialOwner',
      firstName: p.firstName.trim(),
      middleName: normalizeMiddleName(p.middleName),
      lastName: p.lastName.trim(),
      email: p.email.trim(),
      phoneNumber: String(p.phoneNumber || '').replace(/\s+/g, ''),
      birthDate: p.birthDate,
      companyRole: null,
      companyOwnershipPercentage:
        p.companyOwnershipPercentage === null || p.companyOwnershipPercentage === undefined || String(p.companyOwnershipPercentage).trim() === ''
          ? null
          : Number(String(p.companyOwnershipPercentage).trim()),
      isDirector: false,
      companyStartDate: p.companyStartDate,
      address: normalizeAddress(p.address),
    }));
    return [...repPayload, ...ownerPayload];
  }, [representatives, owners]);

  const handleSaveAndContinue = useCallback(async () => {
    if (isLocked || saveInFlightRef.current) return;
    setSubmitError(null);
    setShowValidation(true);
    if (!canSubmit) {
      setSubmitError('Please fill all required fields with valid values.');
      return;
    }

    const payload = buildPayload();
    const payloadJson = JSON.stringify(payload);
    if (lastSavedPayloadJsonRef.current && lastSavedPayloadJsonRef.current === payloadJson && Array.isArray(lastSavedUboPersonsRef.current)) {
      setUboPersons(lastSavedUboPersonsRef.current);
      setShowProofOfAddress(true);
      setShowSummary(false);
      return;
    }

    saveInFlightRef.current = true;
    setSaving(true);
    try {
      const persons = await API.upsertCompanyPersons(payload);
      const { nextReps, nextOwners, selectedIds, requiredUboPersons } = splitApiPersonsToForm(persons);
      setRepresentatives(nextReps);
      setOwners(nextOwners);
      setSelectedManagementIds(selectedIds);
      setUboPersons(requiredUboPersons);
      lastSavedPayloadJsonRef.current = payloadJson;
      lastSavedUboPersonsRef.current = requiredUboPersons;
      setShowProofOfAddress(true);
      setShowSummary(false);
    } catch (e) {
      setSubmitError(e?.message || e?.detail || 'Could not save company persons.');
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
    }
  }, [buildPayload, canSubmit, isLocked]);

  useEffect(() => {
    let cancelled = false;
    if (!showProofOfAddress) {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const people = Array.isArray(uboPersons) ? uboPersons : [];
        const results = await Promise.all(
          people.map(async (p) => {
            try {
              const docs = await API.getCompanyPersonDocuments(p.id);
              const list = Array.isArray(docs) ? docs : [];
              const proof = list.find((d) => {
                const metaType = d?.metadata?.type ?? d?.Metadata?.type ?? null;
                return (
                  String(metaType || '')
                    .trim()
                    .toLowerCase() === PROOF_ADDRESS_METADATA_TYPE.toLowerCase()
                );
              });
              const fileName = proof?.name ?? proof?.Name ?? null;
              const accessUrl = proof?.accessUrl ?? proof?.AccessUrl ?? null;
              const meta = proof?.metadata ?? proof?.Metadata ?? {};
              const existingAddressType = readDocumentSubtypeFromMetadata(meta);
              return {
                personId: p.id,
                existingFileName: fileName,
                accessUrl,
                addressType: toPascalCaseEnumValue(existingAddressType),
              };
            } catch {
              return { personId: p.id, existingFileName: null, accessUrl: null, addressType: '' };
            }
          }),
        );

        if (cancelled) return;
        setProofAddressByPersonId((prev) => {
          const next = { ...(prev || {}) };
          results.forEach((r) => {
            next[r.personId] = {
              ...(next[r.personId] || {}),
              file: null,
              uploading: false,
              uploaded: !!r.existingFileName,
              error: null,
              existingFileName: r.existingFileName,
              accessUrl: r.accessUrl,
              addressType: r.addressType,
            };
          });
          return next;
        });
      } catch (e) {
        if (!cancelled) {
          setSubmitError(e?.message || e?.detail || 'Failed to load proof of address documents.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showProofOfAddress, uboPersons]);

  const allProofOfAddressProvided = useMemo(() => {
    if (!showProofOfAddress) return false;
    const people = Array.isArray(uboPersons) ? uboPersons : [];
    if (people.length === 0) return true;
    return people.every((p) => {
      const st = proofAddressByPersonId?.[p.id];
      const hasFile = !!(st?.existingFileName || st?.uploaded);
      const hasType = !!String(st?.addressType || '').trim();
      return hasFile && hasType;
    });
  }, [showProofOfAddress, uboPersons, proofAddressByPersonId]);

  const handleProofAddressUpload = async (personId, file, addressTypeOverride) => {
    if (isLocked) return;
    if (!personId || !file) return;
    setSubmitError(null);

    const addressType = String(
      addressTypeOverride ?? proofAddressByPersonId?.[personId]?.addressType ?? '',
    ).trim();

    if (!addressType) {
      const previousFileName = proofAddressByPersonId?.[personId]?.existingFileName;
      setProofAddressByPersonId((prev) => ({
        ...(prev || {}),
        [personId]: {
          ...(prev?.[personId] || {}),
          file,
          uploading: false,
          error: null,
          uploaded: false,
          pendingLocal: true,
          existingFileName: null,
          accessUrl: null,
        },
      }));
      if (previousFileName) {
        API.deleteCompanyPersonDocument(personId, previousFileName).catch(() => {});
      }
      return;
    }

    setProofAddressByPersonId((prev) => ({
      ...(prev || {}),
      [personId]: {
        ...(prev?.[personId] || {}),
        file,
        addressType,
        uploading: true,
        error: null,
        uploaded: false,
        pendingLocal: false,
      },
    }));
    try {
      const existingFileName = proofAddressByPersonId?.[personId]?.existingFileName;
      if (existingFileName) {
        await API.deleteCompanyPersonDocument(personId, existingFileName);
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata[type]', PROOF_ADDRESS_METADATA_TYPE);
      formData.append(`metadata[${COMPANY_DOC_TYPE_KEY}]`, addressType);

      const uploaded = await API.uploadCompanyPersonDocument(personId, formData);
      const uploadedName = uploaded?.name ?? uploaded?.Name ?? file?.name ?? null;
      const accessUrl = uploaded?.accessUrl ?? uploaded?.AccessUrl ?? null;
      setProofAddressByPersonId((prev) => ({
        ...(prev || {}),
        [personId]: {
          ...(prev?.[personId] || {}),
          addressType,
          uploading: false,
          uploaded: true,
          error: null,
          pendingLocal: false,
          existingFileName: uploadedName,
          accessUrl,
          file: null,
        },
      }));
    } catch (e) {
      setProofAddressByPersonId((prev) => ({
        ...(prev || {}),
        [personId]: {
          ...(prev?.[personId] || {}),
          uploading: false,
          pendingLocal: true,
          error: e?.message || e?.detail || 'Upload failed.',
        },
      }));
    }
  };

  const handleProofAddressTypeChange = (personId, addressType) => {
    if (isLocked) return;
    const nextType = String(addressType || '').trim();
    const pendingFile = proofAddressByPersonId?.[personId]?.file;

    setProofAddressByPersonId((prev) => ({
      ...(prev || {}),
      [personId]: { ...(prev?.[personId] || {}), addressType: nextType, error: null },
    }));

    if (pendingFile && nextType) {
      handleProofAddressUpload(personId, pendingFile, nextType);
    }
  };

  const handleContinueToOwners = useCallback(async () => {
    if (isLocked || saveInFlightRef.current) return;
    setSubmitError(null);
    setShowValidation(true);

    const repValidationErrors = Array.from(validationErrors).some((errKey) => {
      if (errKey === 'representatives.min' || errKey === 'directors.min') return true;
      return String(errKey).startsWith('representatives.');
    });

    if (repValidationErrors) return;

    saveInFlightRef.current = true;
    setSaving(true);
    try {
      await API.upsertCompanyPersons(buildPayload());
      setShowValidation(false);
      setShowProofOfAddress(false);
      setShowSummary(false);
      setActivePersonsScreen('owners');
      navigate(location.pathname, {
        replace: true,
        state: { ...(location.state ?? {}), companyPersonsScreen: 'owners' },
      });
    } catch (e) {
      setSubmitError(e?.message || e?.detail || 'Could not save company persons.');
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
    }
  }, [buildPayload, isLocked, location.pathname, location.state, navigate, validationErrors]);

  const handleConfirmData = useCallback(() => {
    const isStepCompleted =
      Array.isArray(onboardingStatus?.steps) &&
      onboardingStatus.steps.some(
        (s) =>
          (typeof s === 'string' ? s === 'CollectCompanyPersonData' : s?.step === 'CollectCompanyPersonData') &&
          typeof s === 'object' &&
          s?.isCompleted === true,
      );

    const shouldAdvance = onboardingStatus?.currentStep === 'CollectCompanyPersonData' && !isStepCompleted;

    if (shouldAdvance) {
      hasJustConfirmedRef.current = true;
      dispatch(userActions.advanceOnboardingStep('CollectCompanyPersonData'));
      return;
    }

    const nextPath =
      getPostCompanyPersonDataNextPath(onboardingStatus?.steps, { userType: user?.userType }) ??
      (onboardingStatus?.currentStep ? getOnboardingStepPath(onboardingStatus.currentStep) : null);
    if (nextPath) {
      navigate(nextPath);
    }
  }, [dispatch, navigate, onboardingStatus?.currentStep, onboardingStatus?.steps, user?.userType]);

  const handleBackFromSummary = useCallback(() => {
    setShowSummary(false);
    setShowProofOfAddress(true);
  }, []);

  const handleBackFromProofOfAddress = useCallback(() => {
    setShowProofOfAddress(false);
    setShowSummary(false);
    setActivePersonsScreen('owners');
  }, []);

  const handleContinueFromProofOfAddress = useCallback(() => {
    setShowProofOfAddress(false);
    setShowSummary(true);
  }, []);

  const handleBackFromOwners = useCallback(() => {
    setShowProofOfAddress(false);
    setShowSummary(false);
    setActivePersonsScreen('representatives');
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state ?? {}), companyPersonsScreen: 'representatives' },
    });
  }, [location.pathname, location.state, navigate]);

  const handleBackFromRepresentatives = useCallback(() => {
    navigate('/collect-data', {
      state: { companyCollectSubstep: 'review' },
    });
  }, [navigate]);

  useEffect(() => {
    if (showSummary) {
      setFooterConfig({
        continueLabel: advancingOnboardingStep ? 'Confirming…' : 'Confirm',
        continueDisabled: advancingOnboardingStep,
        onContinue: handleConfirmData,
        backLabel: 'Back',
        onBack: handleBackFromSummary,
      });
      return () => setFooterConfig({});
    }

    if (showProofOfAddress) {
      setFooterConfig({
        continueLabel: 'Continue',
        continueDisabled: !allProofOfAddressProvided,
        onContinue: handleContinueFromProofOfAddress,
        backLabel: 'Back',
        onBack: handleBackFromProofOfAddress,
      });
      return () => setFooterConfig({});
    }

    if (activePersonsScreen === 'owners') {
      setFooterConfig({
        continueLabel: saving ? 'Saving...' : 'Continue',
        continueDisabled: isLocked || saving,
        onContinue: handleSaveAndContinue,
        backLabel: 'Back',
        onBack: handleBackFromOwners,
      });
      return () => setFooterConfig({});
    }

    setFooterConfig({
      continueLabel: saving ? 'Saving...' : 'Continue',
      continueDisabled: isLocked || saving,
      onContinue: handleContinueToOwners,
      backLabel: 'Back',
      onBack: handleBackFromRepresentatives,
    });
    return () => setFooterConfig({});
  }, [
    activePersonsScreen,
    advancingOnboardingStep,
    allProofOfAddressProvided,
    handleBackFromOwners,
    handleBackFromProofOfAddress,
    handleBackFromRepresentatives,
    handleBackFromSummary,
    handleConfirmData,
    handleContinueFromProofOfAddress,
    handleContinueToOwners,
    handleSaveAndContinue,
    isLocked,
    saving,
    setFooterConfig,
    showProofOfAddress,
    showSummary,
  ]);

  if (!user) return null;

  if (showSummary) {
    const formatPersonAddress = (address = {}) =>
      [address.line1, address.line2, address.city, address.stateRegionProvince, address.postCode, address.country].filter(Boolean).join(', ') || '—';

    const handleEditManagers = () => {
      setShowSummary(false);
      setShowProofOfAddress(false);
      setActivePersonsScreen('representatives');
      navigate(location.pathname, {
        replace: true,
        state: { ...(location.state ?? {}), companyPersonsScreen: 'representatives' },
      });
    };

    const handleEditOwners = () => {
      setShowSummary(false);
      setShowProofOfAddress(false);
      setActivePersonsScreen('owners');
      navigate(location.pathname, {
        replace: true,
        state: { ...(location.state ?? {}), companyPersonsScreen: 'owners' },
      });
    };

    return (
      <ReviewStepShell
        icon={<DocumentReviewIcon />}
        title='Review company persons.'
        subtitle='Please confirm before we continue.'
        footer={displayError ? <div className='mt-4'><ErrorMessage error={displayError} /></div> : null}
      >
        {representatives.map((p, idx) => {
          const isLast = owners.length === 0 && idx === representatives.length - 1;
          const fullName = [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ').trim() || '—';
          return (
            <ReviewSection
              key={p.localId}
              title={representatives.length > 1 ? `Company management ${idx + 1}` : 'Company management'}
              isLast={isLast}
              onEdit={handleEditManagers}
              editDisabled={isLocked}
            >
              <ReviewRow label='Name' value={fullName} />
              <ReviewRow label='Email' value={p.email} />
              <ReviewRow label='Role' value={p.companyRole} />
              <ReviewRow label='Phone' value={p.phoneNumber} />
              <ReviewRow label='Birth date' value={p.birthDate} />
              <ReviewRow label='Relationship start day with the company' value={p.companyStartDate} />
              <ReviewRow label='Address' value={formatPersonAddress(p.address)} />
            </ReviewSection>
          );
        })}

        {owners.map((p, idx) => {
          const isLast = idx === owners.length - 1;
          const fullName = [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ').trim() || '—';
          const ownership =
            p.companyOwnershipPercentage == null || p.companyOwnershipPercentage === ''
              ? '—'
              : `${p.companyOwnershipPercentage}%`;
          return (
            <ReviewSection
              key={p.localId}
              title={owners.length > 1 ? `Owner ${idx + 1}` : 'Owner'}
              isLast={isLast}
              onEdit={handleEditOwners}
              editDisabled={isLocked}
            >
              <ReviewRow label='Name' value={fullName} />
              <ReviewRow label='Email' value={p.email} />
              <ReviewRow label='Phone' value={p.phoneNumber} />
              <ReviewRow label='Birth date' value={p.birthDate} />
              <ReviewRow label='Ownership %' value={ownership} />
              <ReviewRow label='Relationship start day with the company' value={p.companyStartDate} />
              <ReviewRow label='Address' value={formatPersonAddress(p.address)} />
            </ReviewSection>
          );
        })}
      </ReviewStepShell>
    );
  }

  if (showProofOfAddress) {
    const people = Array.isArray(uboPersons) ? uboPersons : [];

    return (
      <div className='flex w-full justify-center bg-[#EBF4FB] px-4 pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
        <div className='w-full max-w-[540px] sm:mb-24'>
          <div className='rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-10 sm:py-9'>
            <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
              <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                <polyline points='14 2 14 8 20 8' />
                <line x1='16' y1='13' x2='8' y2='13' />
                <line x1='16' y1='17' x2='8' y2='17' />
              </svg>
            </div>
            <h1 className='mb-2.5 text-center font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Proof of address.</h1>
            <p className='mt-2 text-center text-sm leading-6 text-[#4E6F8A]'>
              Proof of address confirms where business key individuals live. Documents must show a physical address, the
              full name, and be issued within the last 90 days. e.g. bank statement, utility bill, government-issued
              letter, residential lease agreement.
            </p>

            {displayError ? <div className='mt-5'><ErrorMessage error={displayError} /></div> : null}

            {people.length === 0 ? (
              <p className='mt-7 text-center text-sm text-[#4E6F8A]'>No owners found. Go back and add/select at least one owner.</p>
            ) : null}

            <div className={`mt-7 space-y-3 ${isLocked ? 'pointer-events-none opacity-60' : ''}`}>
              <fieldset disabled={isLocked} className='min-w-0 space-y-3 border-0 p-0'>
              {people.map((p) => {
                const st = proofAddressByPersonId?.[p.id] || {};
                const fileInputId = `proof-address-${p.id}`;
                const isUploading = st.uploading;
                const hasPendingLocal = !!(st.file || st.pendingLocal) && !st.existingFileName;
                const isDone = !!st.existingFileName || hasPendingLocal;
                const selectedAddressType = String(st.addressType || '');
                const displayName = st.existingFileName || st.file?.name || 'File uploaded';
                const ownerName = [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || p.email || 'Owner';
                const missingType = hasPendingLocal && !selectedAddressType;
                let uploadZoneClass = 'border-[#C9D8E6] bg-[#F8FAFB] hover:border-[#2E6BAD] hover:bg-[#EBF4FB]/50';
                if (isDone && !isUploading) {
                  uploadZoneClass = 'border-[#A8D5B5] bg-[#F3FAF5]';
                } else if (isUploading) {
                  uploadZoneClass = 'cursor-wait border-[#B8C9D9] bg-[#F8FAFB]';
                }

                return (
                  <div key={p.id} className='rounded-[16px] border-[1.5px] border-[#E2EBF3] bg-white px-[22px] py-5'>
                    <div className='mb-3.5 text-sm font-semibold text-[#0B1929]'>{ownerName}</div>

                    <div className='mb-3.5'>
                      <label
                        htmlFor={`${fileInputId}-type`}
                        className='mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#7A9AB0]'
                      >
                        Document type <span className='text-[#C0392B]'>*</span>
                      </label>
                      <SearchableSelect
                        id={`${fileInputId}-type`}
                        name={`addressType-${p.id}`}
                        value={selectedAddressType}
                        onChange={(e) => handleProofAddressTypeChange(p.id, e.target.value)}
                        placeholder='Select type'
                        options={PROOF_ADDRESS_TYPES}
                        searchPlaceholder='Search document type...'
                        menuPlacement='auto'
                        menuMinWidth='w-full'
                        clearable
                        hideValue
                        disabled={isUploading || isLocked}
                        hasError={missingType}
                        triggerClassName={searchableSelectTriggerClass(missingType)}
                      />
                      {missingType ? (
                        <p className='mt-1.5 text-xs text-[#C0392B]'>Select a document type to finish uploading.</p>
                      ) : null}
                    </div>

                    <input
                      id={fileInputId}
                      type='file'
                      accept='.pdf,.jpg,.jpeg,.png'
                      className='sr-only'
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleProofAddressUpload(p.id, file);
                        e.target.value = '';
                      }}
                      disabled={isUploading || isLocked}
                    />

                    <div
                      role='button'
                      tabIndex={isUploading || isLocked ? -1 : 0}
                      onClick={() => {
                        if (isUploading || isLocked) return;
                        document.getElementById(fileInputId)?.click();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (isUploading || isLocked) return;
                          document.getElementById(fileInputId)?.click();
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isUploading || isLocked) return;
                        const file = e.dataTransfer?.files?.[0];
                        if (file) handleProofAddressUpload(p.id, file);
                      }}
                      className={`block cursor-pointer rounded-[12px] border-[1.5px] border-dashed px-4 py-5 text-center transition ${uploadZoneClass}`}
                    >
                      {isDone ? (
                        <div className='flex items-center justify-center gap-2.5 py-1 text-sm font-semibold text-[#1F7A45]'>
                          <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                            <polyline points='20 6 9 17 4 12' />
                          </svg>
                          <span className='truncate' title={displayName}>
                            {displayName}
                          </span>
                          {st.accessUrl ? (
                            <button
                              type='button'
                              className='shrink-0 text-xs font-medium text-[#2E6BAD] hover:underline'
                              disabled={isUploading}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(st.accessUrl, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              View
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        <>
                          <div className='mx-auto mb-2.5 flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#EBF4FB] text-[#4E6F8A]'>
                            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                              <polyline points='17 8 12 3 7 8' />
                              <line x1='12' y1='3' x2='12' y2='15' />
                            </svg>
                          </div>
                          <div className='text-[13.5px] text-[#3D5A73]'>
                            {isUploading ? (
                              'Uploading…'
                            ) : (
                              <>
                                <span className='font-semibold text-[#0B1929]'>Click to upload</span> or drag and drop
                              </>
                            )}
                          </div>
                          <div className='mt-1 text-xs text-[#7A9AB0]'>PDF, JPG or PNG · Max 10 MB</div>
                        </>
                      )}
                    </div>

                    {st.error ? <p className='mt-2 text-xs text-[#C0392B]'>{st.error}</p> : null}
                  </div>
                );
              })}
              </fieldset>
            </div>

            <div className='mb-1 mt-2 rounded-[12px] bg-[#EBF4FB] px-4 py-3.5 text-[13px] leading-[1.6] text-[#4E6F8A]'>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#7A9AB0'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='mr-1.5 inline align-[-2px]'
                aria-hidden='true'
              >
                <circle cx='12' cy='12' r='10' />
                <line x1='12' y1='8' x2='12' y2='12' />
                <line x1='12' y1='16' x2='12.01' y2='16' />
              </svg>
              All documents are processed securely and stored encrypted in compliance with GDPR.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex w-full justify-center bg-[#EBF4FB] px-4 pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
      <div className='w-full max-w-[540px] sm:mb-24'>
        <div className='rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-10 sm:py-9'>
          <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
            <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
              <circle cx='9' cy='7' r='4' />
              <path d='M23 21v-2a4 4 0 0 0-3-3.87' />
              <path d='M16 3.13a4 4 0 0 1 0 7.75' />
            </svg>
          </div>
          <h1 className='mb-2.5 text-center font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>
            {activePersonsScreen === 'representatives' ? 'Company Management' : 'Company Owners'}
          </h1>
          <p className='mt-2 text-center text-sm leading-6 text-[#4E6F8A]'>
            {activePersonsScreen === 'representatives'
              ? 'Review and edit the details of your company management.'
              : 'Review and edit the details of beneficial owners of your company.'}
          </p>
          {activePersonsScreen === 'owners' ? (
            <p className='mt-2 text-center text-sm leading-6 text-[#4E6F8A]'>
              A company owner (UBO) is a person who directly or indirectly owns 25% or more of the Company in shares or voting rights. If no such person reaches this threshold, please add at least one person with the highest percentage of shares.
            </p>
          ) : null}

          <div className={`mt-7 space-y-4 ${isLocked ? 'pointer-events-none opacity-60' : ''}`}>
            <fieldset disabled={isLocked} className='min-w-0 space-y-4 border-0 p-0'>
            {displayError ? <ErrorMessage error={displayError} /> : null}
            {activePersonsScreen === 'representatives' ? (
              <>
                <div>
                  <div className='mb-3 text-sm font-semibold text-[#0B1929]'>Company management</div>
                  {loadingManagementFromSignicat ? (
                    <div className='mb-3.5 rounded-[16px] border-[1.5px] border-[#E2EBF3] bg-white px-4 py-3'>
                      <div className='flex items-center gap-3'>
                        <div className='h-4 w-4 animate-spin rounded-full border-2 border-[#B8C9D9] border-t-[#2E6BAD]' aria-hidden='true' />
                        <p className='text-sm text-[#4E6F8A]'>Looking up company management…</p>
                      </div>
                    </div>
                  ) : null}
                  {showValidation && validationErrors.has('representatives.min') && (
                    <p className={fieldErrorClass}>At least one company management record is required.</p>
                  )}
                  {representatives.map((p, idx) => (
                    <PersonFields
                      key={p.localId}
                      title={`Company management ${idx + 1}`}
                      person={p}
                      fieldPrefix={`representatives.${idx}`}
                      showValidation={showValidation}
                      validationErrors={validationErrors}
                      disabled={isLocked}
                      showCompanyRoleField
                      showOwnershipField={p.companyPersonType === 'BeneficialControlling'}
                      addressAllowedCountries={[]}
                      onChange={(next) => setRepresentatives((prev) => prev.map((x, i) => (i === idx ? next : x)))}
                      onRemove={
                        !isLocked && representatives.length > 1
                          ? () => {
                              setRepresentatives((prev) => prev.filter((_, i) => i !== idx));
                              setCollapsedIds((prev) => {
                                const next = new Set(prev);
                                next.delete(p.localId);
                                return next;
                              });
                            }
                          : undefined
                      }
                    />
                  ))}
                </div>

                <button
                  type='button'
                  onClick={() => setRepresentatives((prev) => [...prev, makeEmptyPerson('ControllingPerson')])}
                  className={addPersonButtonClass}
                  disabled={isLocked}
                >
                  <PlusIcon className='h-4 w-4' />
                  Add Another Company management
                </button>
              </>
            ) : (
              <div>
                <div className='mb-3 text-sm font-semibold text-[#0B1929]'>Owners</div>
                {showValidation && validationErrors.has('owners.min') && (
                  <p className={fieldErrorClass}>At least one company owner (UBO) is required.</p>
                )}
                <div className='mb-3.5 rounded-[16px] border-[1.5px] border-[#E2EBF3] bg-white px-5 py-4'>
                  <div className='mb-3 flex items-start justify-between gap-4'>
                    <div>
                      <h4 className='text-sm font-semibold text-[#0B1929]'>Company management list</h4>
                      <p className='mt-1 text-sm text-[#4E6F8A]'>Select any company management people who are also owners.</p>
                    </div>
                    {selectedManagementIds.size > 0 ? (
                      <button
                        type='button'
                        onClick={() => {
                          if (isLocked) return;
                          const ids = new Set(selectedManagementIds);
                          setSelectedManagementIds(new Set());
                          setRepresentatives((repsPrev) =>
                            repsPrev.map((r) => {
                              if (!ids.has(r.localId)) return r;
                              return { ...r, companyPersonType: 'ControllingPerson', companyOwnershipPercentage: '' };
                            }),
                          );
                        }}
                        className='shrink-0 text-sm font-medium text-[#2E6BAD] hover:underline disabled:cursor-not-allowed disabled:opacity-50'
                        disabled={isLocked}
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>

                  <div className='grid grid-cols-1 gap-2'>
                    {representatives
                      .filter((p) => String(p.firstName || p.lastName || p.email || '').trim().length > 0)
                      .map((p) => {
                        const isSelected = selectedManagementIds.has(p.localId);
                        const fullName = [p.firstName, p.middleName, p.lastName].filter((v) => String(v ?? '').trim().length > 0).join(' ');
                        const label = fullName || p.email || 'Company management';
                        const checkboxId = `company-management-select-${p.localId}`;
                        const showOwnerValidation =
                          showValidation &&
                          validationErrors.has(
                            `representatives.${representatives.findIndex((r) => r.localId === p.localId)}.companyOwnershipPercentage`,
                          );
                        return (
                          <div
                            key={p.localId}
                            className={`rounded-[12px] border-[1.5px] px-3 py-2.5 transition ${
                              isSelected ? 'border-[#2E6BAD] bg-[#EBF4FB]' : 'border-[#E2EBF3] hover:bg-[#F8FAFB]'
                            }`}
                          >
                            <div className='flex items-center gap-3'>
                              <input
                                id={checkboxId}
                                type='checkbox'
                                checked={isSelected}
                                onChange={() => toggleManagementSelection(p.localId)}
                                disabled={isLocked}
                                className='h-4 w-4 accent-[#2E6BAD] disabled:cursor-not-allowed'
                              />
                              <label htmlFor={checkboxId} className='min-w-0 cursor-pointer'>
                                <div className='truncate text-sm font-medium text-[#0B1929]'>{label}</div>
                                <div className='truncate text-xs text-[#7A9AB0]'>{p.email || '—'}</div>
                              </label>
                            </div>

                            {isSelected ? (
                              <div className='mt-3 pl-7'>
                                <label htmlFor={`${checkboxId}-ownership`} className={fieldLabelClass}>
                                  Ownership % *
                                </label>
                                <input
                                  id={`${checkboxId}-ownership`}
                                  inputMode='numeric'
                                  type='number'
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={p.companyOwnershipPercentage ?? ''}
                                  onChange={(e) => {
                                    if (isLocked) return;
                                    const v = e.target.value;
                                    setRepresentatives((prevReps) =>
                                      prevReps.map((r) =>
                                        r.localId === p.localId
                                          ? { ...r, companyOwnershipPercentage: v, companyPersonType: 'BeneficialControlling' }
                                          : r,
                                      ),
                                    );
                                  }}
                                  disabled={isLocked}
                                  className={`${inputClass} ${showOwnerValidation ? inputErrorBorderClass : ''}`}
                                />
                                <p className='mt-1 text-xs text-[#7A9AB0]'>Enter 0–100.</p>
                                {showOwnerValidation ? <p className={fieldErrorClass}>Ownership % is required (0–100).</p> : null}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                  </div>
                </div>
                {representatives.some((r) => r.companyPersonType === 'BeneficialControlling') ? (
                  <div className='mb-3.5'>
                    <div className='mb-3'>
                      <h4 className='text-sm font-semibold text-[#0B1929]'>Selected owners from company management</h4>
                      <p className='mt-1 text-sm text-[#4E6F8A]'>
                        These people are owners too. Their details are copied from the management section.
                      </p>
                    </div>
                    {representatives
                      .map((r, idx) => ({ r, idx }))
                      .filter(({ r }) => r.companyPersonType === 'BeneficialControlling')
                      .map(({ r, idx }) => (
                        <PersonFields
                          key={`rep-owner-${r.localId}`}
                          title='Owner (company management)'
                          person={r}
                          fieldPrefix={`representatives.${idx}`}
                          showValidation={showValidation}
                          validationErrors={validationErrors}
                          disableIdentityFields
                          disabled={isLocked}
                          showOwnershipField={false}
                          addressAllowedCountries={[]}
                          onChange={(next) => setRepresentatives((prev) => prev.map((x, i) => (i === idx ? next : x)))}
                          collapsed={false}
                        />
                      ))}
                  </div>
                ) : null}
                {owners.map((p, idx) => (
                  <div key={p.localId} data-owner-localid={p.localId}>
                    <PersonFields
                      title={`Owner ${idx + 1}`}
                      person={p}
                      fieldPrefix={`owners.${idx}`}
                      showValidation={showValidation}
                      validationErrors={validationErrors}
                      disabled={isLocked}
                      showOwnershipField
                      addressAllowedCountries={[]}
                      onChange={(next) => setOwners((prev) => prev.map((x, i) => (i === idx ? next : x)))}
                      onRemove={
                        !isLocked && owners.length > 0
                          ? () => {
                              setOwners((prev) => prev.filter((_, i) => i !== idx));
                              setCollapsedIds((prev) => {
                                const next = new Set(prev);
                                next.delete(p.localId);
                                return next;
                              });
                            }
                          : undefined
                      }
                    />
                  </div>
                ))}

                <button
                  type='button'
                  onClick={() => {
                    if (isLocked) return;
                    setSubmitError(null);
                    setShowValidation(false);
                    setOwners((prev) => [...prev, makeEmptyPerson('BeneficialOwner')]);
                  }}
                  className={addPersonButtonClass}
                  disabled={isLocked}
                >
                  <PlusIcon className='h-4 w-4' />
                  Add Another Owner
                </button>
              </div>
            )}
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CollectCompanyPersonData;
